import { featureEnabled, limitOf, type EffectiveEntitlementsT, type FeatureKeyT, type LimitKeyT } from "@malfak/contracts";
import { useApiQuery } from "@/lib/api/hooks";

/**
 * Plans & entitlements (ALP-81) on the web: one query (`entitlements`), shared by every gate.
 *
 * STABLE API (other features import these; the plans web track may change internals, not signatures):
 * - `useEntitlements()` → the query (data = EffectiveEntitlements)
 * - `useFeature(key)` → `{ enabled, loading }` (while loading or on error we do NOT hide anything: the API still enforces)
 * - `useLimit(key)` → the EffectiveLimit (or undefined while loading)
 * - `<Gate feature="…">` in ./gate.tsx: renders children when enabled, else a disabled state with the localized reason
 */
export function useEntitlements() {
  return useApiQuery("entitlements", undefined, { staleTime: 60_000 });
}

export function useFeature(key: FeatureKeyT): { enabled: boolean; loading: boolean } {
  const q = useEntitlements();
  if (!q.data) return { enabled: true, loading: q.isLoading };
  return { enabled: featureEnabled(q.data as EffectiveEntitlementsT, key), loading: false };
}

export function useLimit(key: LimitKeyT) {
  const q = useEntitlements();
  return q.data ? limitOf(q.data as EffectiveEntitlementsT, key) : undefined;
}
